import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Guide from "@/components/Guide";
import Table from "@/components/Table";

import DeliveryModal from "@/modals/DeliveryModal";
import CommentsModal from "@/modals/CommentsModal";
import CsFormModal from "@/modals/CsFormModal";

import { QUERY_SORT } from "@/stores/Cses";
import {
  useCsesStore,
  useLoadingStore,
  useModalStore,
} from "@/stores/hooks";

const BodyContent = styled.div``;
const Container = styled.div`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 40px;
`;

const SearchWrap = styled.div`
  padding-top: 15px;
`;
const SearchRow = styled(Row)`
  margin-bottom: 10px;
`;
const Column = styled(Col)`
  display: flex;
  align-items: center;
`;
const ColumnRight = styled(Column)`
  justify-content: flex-end;
`;
const SearchButton = styled(Button)`
  margin-left: 5px;
`;
const ActionButton = styled(Button)`
  margin-left: 4px;
`;
const FormControl = styled(Form.Control)`
  width: ${({ width }) => width || 180}px !important;
`;
const Wave = styled.span`
  margin: 0 5px;
`;
const SearchLabel = styled.div`
  font-weight: bold;
  margin-right: 10px;
`;
const TabWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;
const TabLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-weight: ${({ active }) => (active ? "bold" : "normal")};
  cursor: pointer;
`;
const TableWrap = styled.div``;
const ColumnLink = styled.div`
  cursor: pointer;
  text-decoration: underline;
`;

const SORT_DATE = "date";

const SORT_OPTIONS = [
  { value: SORT_DATE, label: "CS등록일자" },
  { value: QUERY_SORT.UNCONFIRMED_COMMENT, label: "미확인코멘트" },
  { value: QUERY_SORT.COMMENT_CREATED_AT, label: "코멘트" },
];

const FILTER_TABS = [
  {
    id: "all",
    label: "전체",
    query: {
      checked: undefined,
      deleted: false,
    },
  },
  {
    id: "pending",
    label: "처리대기",
    query: {
      checked: false,
      deleted: false,
    },
  },
  {
    id: "done",
    label: "처리완료",
    query: {
      checked: true,
      deleted: false,
    },
  },
  {
    id: "canceled",
    label: "취소",
    query: {
      checked: undefined,
      deleted: true,
    },
  },
];

const getSortDropdownValue = (sort) => {
  if (sort === QUERY_SORT.UNCONFIRMED_COMMENT) {
    return QUERY_SORT.UNCONFIRMED_COMMENT;
  }

  if (sort === QUERY_SORT.COMMENT_CREATED_AT) {
    return QUERY_SORT.COMMENT_CREATED_AT;
  }

  return SORT_DATE;
};

const getActiveTabId = (query) => {
  if (query.deleted === true) {
    return "canceled";
  }

  if (query.checked === false) {
    return "pending";
  }

  if (query.checked === true) {
    return "done";
  }

  return "all";
};

const mapSearchKeyword = (value) => {
  const keyword = (value || "").trim();

  if (!keyword) {
    return { bookId: "", receiverName: "", receiverMobile: "" };
  }

  const digits = keyword.replace(/[-\s]/g, "");

  if (/^01\d{8,9}$/.test(digits) || /^0\d{9,10}$/.test(digits)) {
    return { bookId: "", receiverName: "", receiverMobile: keyword };
  }

  if (/^\d+$/.test(digits) && digits.length >= 8) {
    return { bookId: keyword, receiverName: "", receiverMobile: "" };
  }

  return { bookId: "", receiverName: keyword, receiverMobile: "" };
};

const BookIdColumn = memo(({ bookId, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClick = useCallback(async () => {
    if (!bookId) {
      return;
    }

    openModal(
      <DeliveryModal
        key={`delivery-${bookId}-modal`}
        bookId={bookId}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  }, [bookId, onAfterSubmit, openModal]);

  return <ColumnLink onClick={handleClick}>{bookId}</ColumnLink>;
});

const CsActionsColumn = memo(({ cs, onAfterSubmit }) => {
  const { openModal } = useModalStore();
  const { cancel } = useCsesStore();
  const { startLoading, finishLoading } = useLoadingStore();

  const handleEdit = useCallback(() => {
    if (!cs.canCorpEdit()) {
      window.alert("처리가 완료된 CS는 수정할 수 없습니다.");
      return;
    }

    openModal(
      <CsFormModal
        key={`cs-edit-${cs.id}`}
        cs={cs}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  }, [cs, onAfterSubmit, openModal]);

  const handleOpenComments = useCallback(() => {
    const bookId = cs.renderBookId();

    if (!bookId) {
      window.alert("송장번호가 없어 코멘트를 조회할 수 없습니다.");
      return;
    }

    openModal(
      <CommentsModal
        key={`cs-comments-${bookId}`}
        bookId={bookId}
      />,
    );
  }, [cs, openModal]);

  const handleCancel = useCallback(async () => {
    if (!window.confirm(`${cs.renderBookId()} 건의 CS를 취소하시겠습니까?`)) {
      return;
    }

    try {
      startLoading();
      await cancel(cs.id);
      window.alert("CS가 취소되었습니다.");

      if (onAfterSubmit) {
        await onAfterSubmit();
      }
    } catch (e) {
      window.alert(`CS 취소에 실패했습니다.\n${e.message}`);
    }

    finishLoading();
  }, [cancel, cs, finishLoading, onAfterSubmit, startLoading]);

  return (
    <div>
      <ActionButton type="button" width="70px" onClick={handleOpenComments}>
        코멘트
      </ActionButton>
      {cs.canCorpEdit() && (
        <>
          <ActionButton type="button" width="60px" onClick={handleEdit}>
            수정
          </ActionButton>
          <ActionButton type="button" variant="secondary" width="60px" onClick={handleCancel}>
            취소
          </ActionButton>
        </>
      )}
    </div>
  );
});

export default function Cses() {
  const { state, ...actions } = useCsesStore();
  const { startLoading, finishLoading } = useLoadingStore();
  const { openModal } = useModalStore();
  const { handleSubmit } = useForm();
  const [keyword, setKeyword] = useState("");

  const fetchAll = useCallback(
    async (queryOverride) => {
      try {
        startLoading();
        await actions.fetchAll(queryOverride);
      } catch (e) {
        window.alert(`CS 내역 조회에 실패했습니다.\n${e.message}`);
      }

      finishLoading();
    },
    [actions.fetchAll, finishLoading, startLoading],
  );

  useEffect(() => {
    fetchAll();
  }, [
    state.query.page,
    state.query.pageSize,
    state.query.sort,
    state.query.checked,
    state.query.deleted,
  ]);

  const handleChangeDate = useCallback(
    (e) => {
      actions.setQuery({ [e.target.name]: e.target.value });

      if (
        e.target.name === "createdAtFrom" &&
        e.target.value > state.query.createdAtTo
      ) {
        actions.setQuery({ createdAtTo: e.target.value });
      }

      if (
        e.target.name === "createdAtTo" &&
        e.target.value < state.query.createdAtFrom
      ) {
        actions.setQuery({ createdAtFrom: e.target.value });
      }
    },
    [actions.setQuery, state.query],
  );

  const handleChangeKeyword = useCallback((e) => {
    setKeyword(e.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    const nextQuery = {
      ...state.query,
      page: 1,
      ...mapSearchKeyword(keyword),
    };

    actions.setQuery(nextQuery);
    fetchAll(nextQuery);
  }, [actions.setQuery, fetchAll, keyword, state.query]);

  const handleSelectTab = useCallback(
    (tab) => {
      const dropdownSort = getSortDropdownValue(state.query.sort);
      const nextSort =
        dropdownSort === SORT_DATE ? QUERY_SORT.CREATED_AT : dropdownSort;

      actions.setQuery({
        page: 1,
        ...tab.query,
        sort: nextSort,
      });
    },
    [actions.setQuery, state.query.sort],
  );

  const handleChangeSort = useCallback(
    (e) => {
      const value = e.target.value;
      const sort = value === SORT_DATE ? QUERY_SORT.CREATED_AT : value;

      actions.setQuery({
        page: 1,
        sort,
      });
    },
    [actions.setQuery],
  );

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSelect = useCallback(() => {}, []);

  const handleOpenCreate = useCallback(() => {
    openModal(
      <CsFormModal key="cs-create-modal" onAfterSubmit={fetchAll} />,
    );
  }, [fetchAll, openModal]);

  const activeTabId = getActiveTabId(state.query);

  const columns = useMemo(
    () => [
      {
        Header: "등록구분",
        accessor: (row) => row.renderRegistrationType(),
        width: 80,
      },
      {
        Header: "배송상태",
        accessor: (row) => row.renderDeliveryStatus(),
        width: 90,
      },
      {
        Header: "송장번호",
        accessor: (row) => (
          <BookIdColumn bookId={row.renderBookId()} onAfterSubmit={fetchAll} />
        ),
        width: 110,
      },
      {
        Header: "주문번호",
        accessor: (row) => row.renderOrderIdFromCorp(),
        width: 130,
      },
      {
        Header: "주문번호서브",
        accessor: (row) => row.renderOrderIdFromCorpSub(),
        width: 90,
      },
      {
        Header: "CS내용",
        accessor: (row) => row.renderText(),
        width: 180,
      },
      {
        Header: "기사님 답변",
        accessor: (row) => row.renderAnswer(),
        width: 160,
      },
      {
        Header: "CS등록일시",
        accessor: (row) => row.renderCreatedAt(),
        width: 130,
      },
      {
        Header: "CS처리여부",
        accessor: (row) => row.renderChecked(),
        width: 90,
      },
      {
        Header: "CS처리일시",
        accessor: (row) => row.renderCompletedAt(),
        width: 130,
      },
      {
        Header: "최근코멘트작성자",
        accessor: (row) => row.renderLatestCommentWriterName(),
        width: 120,
      },
      {
        Header: "최근코멘트작성일시",
        accessor: (row) => row.renderLatestCommentCreatedAt(),
        width: 140,
      },
      {
        Header: "코멘트확인여부",
        accessor: (row) => row.renderCommentConfirmed(),
        width: 110,
      },
      {
        Header: "관리",
        accessor: (row) => (
          <CsActionsColumn cs={row} onAfterSubmit={fetchAll} />
        ),
        width: 210,
      },
    ],
    [fetchAll],
  );

  return (
    <BodyContent>
      <Guide />
      <Container>
        <SearchWrap>
          <SearchRow>
            <Column xs={8}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <SearchLabel>CS등록일자</SearchLabel>
                <FormControl
                  name="createdAtFrom"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.createdAtFrom}
                />
                <Wave>~</Wave>
                <FormControl
                  name="createdAtTo"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.createdAtTo}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </Column>
            <ColumnRight xs={4}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <FormControl
                  width={260}
                  name="keyword"
                  onChange={handleChangeKeyword}
                  placeholder="송장번호, 수하인명, 연락처 조회"
                  value={keyword}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </ColumnRight>
          </SearchRow>
          <SearchRow>
            <Column xs={8}>
              <TabWrap>
                {FILTER_TABS.map((tab) => (
                  <TabLabel key={tab.id} active={activeTabId === tab.id}>
                    <input
                      checked={activeTabId === tab.id}
                      name="cs-filter-tab"
                      onChange={() => handleSelectTab(tab)}
                      type="radio"
                    />
                    {tab.label}
                  </TabLabel>
                ))}
              </TabWrap>
            </Column>
            <ColumnRight xs={4}>
              <SearchLabel>전체 총 {state.totalCount} 조회</SearchLabel>
              <FormControl
                width={140}
                as="select"
                name="sort"
                onChange={handleChangeSort}
                value={getSortDropdownValue(state.query.sort)}
                custom
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormControl>
              <SearchButton type="button" width="100px" onClick={handleOpenCreate}>
                CS등록
              </SearchButton>
            </ColumnRight>
          </SearchRow>
        </SearchWrap>
        <TableWrap>
          <Table
            responsive
            bordered
            hover
            columns={columns}
            currentPage={state.query.page}
            data={state.cses}
            goToPage={handleGoToPage}
            nextPage={handleNextPage}
            onSelect={handleSelect}
            pageCount={state.pageCount}
            pageSize={state.query.pageSize}
            previousPage={handlePreviousPage}
            showPageSize={false}
          />
        </TableWrap>
      </Container>
    </BodyContent>
  );
}
